import React, { useEffect, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import { ProductDownloadsModel } from "./ProductDownloadsModel";
import DownloadsTable from "../DownloadsTable/DownloadsTable";
import { DownloadModel } from "../Download/DownloadModel";
import { Theme } from "../../core/Models/CustomSiteConfigModel";
import { themeService } from "../../core/NotificationStripNav/ThemeService";

import "./ProductDownloads.css";

const ProductDownloads: React.FC<{
  blok: ProductDownloadsModel;
  searchTerm: string;
}> = ({ blok, searchTerm }) => {
  const [theme, setTheme] = useState<Theme | null>(themeService.getTheme());
  const [isLegacy, setIsLegacy] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [activeDownloads, setActiveDownloads] = useState<DownloadModel[]>([]);
  const [legacyDownloads, setLegacyDownloads] = useState<DownloadModel[]>([]);
  const order = ["Mac", "Windows", "iOS", "Android", "Browser"];

  const sortedDownloads = blok.downloads.sort((a, b) => {
    return order.indexOf(a.os) - order.indexOf(b.os);
  });

  useEffect(() => {
    const unsubscribe = themeService.subscribe(setTheme);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const filtered = sortedDownloads.filter(
      (item) =>
        item.downloadName
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase()) ||
        item.minimalOs.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
    setActiveDownloads(filtered.filter((x) => x.versionType !== "legacy"));
    setLegacyDownloads(filtered.filter((x) => x.versionType === "legacy"));
    setIsLegacy(!!debouncedSearchTerm);
  }, [debouncedSearchTerm, sortedDownloads]);

  if (!blok.downloads) {
    return null;
  }

  if (
    (!activeDownloads || activeDownloads.length < 1) &&
    (!legacyDownloads || legacyDownloads.length < 1)
  ) {
    return null;
  }

  return (
    <div {...storyblokEditable(blok)} className={`product-downloads-comp`}>
      <div className="download-title">{blok.productName}</div>
      <DownloadsTable
        downloads={activeDownloads}
        key={blok._uid}
      ></DownloadsTable>
      <div className="show-legacy-container">
        <span
          className="underline-button"
          style={{
            color: theme?.textColor,
          }}
          onClick={() => setIsLegacy(!isLegacy)}
        >
          {isLegacy ? "HIDE LEGACY VERSIONS" : "SHOW LEGACY VERSIONS"}
        </span>
        {!isLegacy && (
          <span className="arrow" onClick={() => setIsLegacy(true)}>
            &#8595;
          </span>
        )}
        {isLegacy && (
          <span className="arrow" onClick={() => setIsLegacy(false)}>
            &#8593;
          </span>
        )}
      </div>

      {isLegacy && legacyDownloads.length > 0 && (
        <DownloadsTable
          downloads={legacyDownloads}
          key={`legacy-${blok._uid}`}
        ></DownloadsTable>
      )}
    </div>
  );
};

export default ProductDownloads;
